import React, { useState, useMemo, useEffect, createRef } from "react";
import { Source, Layer } from "react-map-gl";

import { ICoordinate, IMarker } from "shared/types";
import { BaseMap } from "shared/components";
import {
  DEFAULT_COORDINATES,
  DEFAULT_FOCUS_ZOOM,
  DEFAULT_ZOOM,
  getMarkersCenter,
  mapMarkersToGeoJson,
  useLoadImageRef,
} from "shared/components/Map/utils";
import {
  clusterLayer,
  clusterCountLayer,
  unclusteredPointLayer,
} from "./layers";
import { MapContainerProps } from "../BaseMap/styles";

interface ClusterMapProps extends MapContainerProps {
  markers: IMarker[];
  activeMarker?: IMarker;
  setActiveMarker?: (id: string) => void;
}

export const ClusterMap: React.FC<ClusterMapProps> = ({
  markers,
  activeMarker,
  setActiveMarker,
  ...props
}) => {
  const mapRef = createRef<any>();
  const [mapProps, setMapProps] = useState<{
    coordinates?: ICoordinate;
    zoom?: number;
  }>({ zoom: DEFAULT_ZOOM, coordinates: DEFAULT_COORDINATES });

  useEffect(() => {
    const coordinates = getMarkersCenter(markers);
    if (coordinates) {
      setMapProps((props) => ({ ...props, coordinates }));
    }
  }, [markers]);

  useEffect(() => {
    if (activeMarker) {
      setMapProps({
        coordinates: activeMarker.coordinates,
        zoom: DEFAULT_FOCUS_ZOOM,
      });
    }
  }, [activeMarker]);

  // load icon image
  useLoadImageRef(mapRef);

  const geojson: GeoJSON.FeatureCollection<GeoJSON.Geometry> = useMemo(
    () => mapMarkersToGeoJson(markers),
    [markers]
  );

  const onClick = (event: any) => {
    if (mapRef?.current) {
      const feature = event?.features?.[0];
      const clusterId = feature?.properties?.cluster_id;

      if (feature?.properties?.cluster && clusterId) {
        const mapboxSource = mapRef.current.getMap().getSource("venues");

        mapboxSource.getClusterExpansionZoom(
          clusterId,
          (err: any, zoom: number) => {
            if (err) {
              return;
            }
            setMapProps({
              coordinates: {
                longitude: feature.geometry.coordinates[0],
                latitude: feature.geometry.coordinates[1],
              },
              zoom,
            });
          }
        );
      } else {
        const id = feature?.properties?.id;
        if (id && setActiveMarker) {
          setActiveMarker(id);
        }
      }
    }
  };

  return (
    <BaseMap {...mapProps} onClick={onClick} mapRef={mapRef} {...props}>
      <Source id="venues" type="geojson" data={geojson} cluster={true}>
        <Layer {...clusterLayer} />
        <Layer {...clusterCountLayer} />
        <Layer {...unclusteredPointLayer} />
      </Source>
    </BaseMap>
  );
};
